import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { filter, take } from 'rxjs/operators';
import { ChangeActionCORSidebarComponent } from '@app/framework/interaction-bar/prime-commitments/change-actions/change-action-cor-sidebar/change-action-cor-sidebar.component';
import { CORDetail, PcoCorModel } from '@app/store/prime-commitments/prime-commitments.types';
import {
  CHANGE_ACTIONS_TYPE,
  getEmptyPotentialChangeLineItem,
} from '@app/store/prime-commitments/prime-commitments.constants';
import { primeChangesSidebarActions } from '@app/store/prime-commitments/prime-commitments.actions';
import { DeepCopyService } from '@app/services/deep-copy.service';
import { primeDetailsSelectors } from '@app/store/prime-commitments/prime-commitments.selectors';
import { ChangeActionPCOSidebarComponent } from '@app/framework/interaction-bar/prime-commitments/change-actions/change-action-pco-sidebar/change-action-pco-sidebar.component';
import { ChangeActionsStore } from '@app/store/change-actions/change-actions.store';
import { ChangeActionGeneralSidebarComponent } from '@app/framework/interaction-bar/prime-commitments/change-actions/change-action-general-sidebar/change-action-general-sidebar.component';
import { pcoActions } from '@app/store/pco/pco.actions';
import { pcoSelectors } from '@app/store/pco/pco.selectors';
import { PCO_STATUS, PcoDetail, PcoDetailUpdateReq } from '@app/store/pco/pco.types';
import { EMPTY_POTENTIAL_CHANGE_ORDER_GENERAL } from '@app/store/pco/pco.constants';

@Component({
  selector: 'app-change-action-sidebar',
  imports: [
    ChangeActionPCOSidebarComponent,
    ChangeActionCORSidebarComponent,
    ChangeActionGeneralSidebarComponent,
  ],
  templateUrl: './change-action-sidebar.component.html',
  styleUrl: './change-action-sidebar.component.scss',
})
export class ChangeActionSidebarComponent implements OnInit, OnDestroy {
  private readonly store = inject(Store);
  private readonly changeActionsStore = inject(ChangeActionsStore);

  pcoDetail$ = this.store.select(pcoSelectors.selectPCODetailsSidebar);
  corDetail$ = this.store.select(primeDetailsSelectors.selectCORDetailsSidebar);
  pcoDetail: PcoDetailUpdateReq = null;
  corDetail: CORDetail = null;

  @Input() data: {
    isEdit: boolean;
    changeActionType?:
      | CHANGE_ACTIONS_TYPE.POTENTIAL_CHANGE_ORDER
      | CHANGE_ACTIONS_TYPE.CHANGE_ORDER_REQUEST;
  };
  currentPage: 1 | 2 = 1;
  model: PcoCorModel = {
    generalData: DeepCopyService.deepCopy(EMPTY_POTENTIAL_CHANGE_ORDER_GENERAL),
    selectedPCOIds: [],
    lineItems: [],
  };

  ngOnInit() {
    this.store.dispatch(primeChangesSidebarActions.onChangeActionSidebarOpen());
    if (this.data.changeActionType === CHANGE_ACTIONS_TYPE.POTENTIAL_CHANGE_ORDER) {
      this.changeActionsStore.pcoIsEditMode(this?.data?.isEdit);
    } else {
      this.changeActionsStore.corIsEditMode(this?.data?.isEdit);
    }

    if (!this?.data?.isEdit) {
      this.model.lineItems = [getEmptyPotentialChangeLineItem()];
      return;
    }

    // then load data from PCO or COR Details
    if (this.data.changeActionType === CHANGE_ACTIONS_TYPE.POTENTIAL_CHANGE_ORDER) {
      this.pcoDetail$.pipe(take(1)).subscribe((pcoDetail: PcoDetail) => {
        this.pcoDetail = pcoDetail;
        this.model = {
          generalData: {
            type: CHANGE_ACTIONS_TYPE.POTENTIAL_CHANGE_ORDER,
            files: pcoDetail.files,
            id: pcoDetail.id,
            title: pcoDetail.title,
            number: pcoDetail.number,
            status: pcoDetail.status,
            date: pcoDetail.date,
            duration_extension: String(pcoDetail.duration_extension),
            description: pcoDetail.description,
          },
          lineItems: [], // line items are not editable, on save it will be loaded in the store with the original values
          selectedPCOIds: [],
        };
      });
    } else {
      this.corDetail$
        .pipe(
          filter((data) => !!data),
          take(1),
        )
        .subscribe((corDetail) => {
          this.corDetail = corDetail;
          this.model = {
            generalData: {
              type: CHANGE_ACTIONS_TYPE.CHANGE_ORDER_REQUEST,
              files: corDetail.files,
              id: corDetail.id,
              title: corDetail.title,
              number: corDetail.number,
              status: corDetail.status,
              date: corDetail.date,
              duration_extension: null,
              description: corDetail.description,
            },
            lineItems: [],
            selectedPCOIds: corDetail.pcos.map((pco) => pco.id),
          };
        });
    }
  }

  ngOnDestroy() {
    this.store.dispatch(primeChangesSidebarActions.onChangesSidebarClosed());
  }

  decrementPage() {
    if (this.currentPage === 1) {
      return;
    }

    this.currentPage--;
  }

  incrementPage() {
    this.currentPage++;
  }

  createPco() {
    this.store.dispatch(pcoActions.onPCOSubmit({ model: DeepCopyService.deepCopy(this.model) }));
  }

  /**
   * Save or Update the Change Order Request
   */
  saveCOR() {
    this.store.dispatch(
      primeChangesSidebarActions.onCORSubmit({
        model: DeepCopyService.deepCopy(this.model),
      }),
    );
  }

  updatePco() {
    this.pcoDetail = {
      ...this.pcoDetail,
      id: this.model.generalData.id,
      files: this.model.generalData.files,
      title: this.model.generalData.title,
      number: this.model.generalData.number,
      status: this.model.generalData.status as PCO_STATUS,
      date: this.model.generalData.date,
      duration_extension: this.model.generalData.duration_extension,
      description: this.model.generalData.description,
    };

    this.store.dispatch(
      pcoActions.onPCOChangeActionFirstPageSync({
        PCO: DeepCopyService.deepCopy(this.pcoDetail),
      }),
    );
  }
}
